import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Gift, XCircle, Receipt } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

const STATUS_META = {
  paid:    { key: "payment.status.paid",    color: "text-verdict-real",    border: "border-verdict-real/30",    bg: "bg-verdict-real/5",    icon: CheckCircle2 },
  pending: { key: "payment.status.pending", color: "text-verdict-uncertain", border: "border-verdict-uncertain/30", bg: "bg-verdict-uncertain/5", icon: Clock },
  failed:  { key: "payment.status.failed",  color: "text-verdict-fake",    border: "border-verdict-fake/30",    bg: "bg-verdict-fake/5",    icon: XCircle },
};

const PLAN_LABELS = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };
const PLAN_COLORS = { starter: "text-emerald-400", pro: "text-brand-primary", business: "text-purple-400" };

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const item      = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function PaymentHistory() {
  const [txns, setTxns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    axios.get(`${API}/payment-history`, { withCredentials: true })
      .then((r) => setTxns(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen" data-testid="payment-history-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("nav.account")}</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            {t("payment.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("payment.subtitle")}</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : txns.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-20 space-y-3 border border-border rounded-md bg-card">
            <Receipt className="w-10 h-10 text-muted-foreground/30 mx-auto" />
            <p className="text-muted-foreground text-sm">{t("payment.empty")}</p>
            <Link to="/pricing">
              <Button size="sm" variant="outline" className="rounded-sm mt-2">{t("payment.empty.cta")}</Button>
            </Link>
          </motion.div>
        ) : (
          <>
            {/* Summary cards */}
            <motion.div variants={container} initial="hidden" animate="show"
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: t("payment.summary.total"),   value: txns.length },
                { label: t("payment.summary.success"), value: txns.filter(t => t.payment_status === "paid").length },
                { label: t("payment.summary.paid"),    value: `Rp ${txns.filter(tx => tx.payment_status === "paid").reduce((s, tx) => s + (tx.amount || 0), 0).toLocaleString("id-ID")}` },
                { label: t("payment.summary.active"),  value: PLAN_LABELS[txns.find(tx => tx.payment_status === "paid")?.plan_id] || "Free" },
              ].map((s) => (
                <motion.div key={s.label} variants={item}
                  className="rounded-md border border-border bg-card p-4">
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wide">{s.label}</p>
                  <p className="font-heading text-xl font-bold mt-1">{s.value}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Transaction list */}
            <motion.div variants={container} initial="hidden" animate="show" className="space-y-3">
              {txns.map((tx) => {
                const isTrial = tx.type === "trial";
                const st = isTrial && tx.payment_status === "paid"
                  ? {
                      key: "payment.status.trialActive",
                      color: "text-brand-primary",
                      border: "border-brand-primary/30",
                      bg: "bg-brand-primary/10",
                      icon: Gift,
                    }
                  : (STATUS_META[tx.payment_status] || STATUS_META.pending);
                const Icon = st.icon;
                const planColor = PLAN_COLORS[tx.plan_id] || "text-muted-foreground";
                const amountLabel = isTrial
                  ? t("payment.amount.free", { defaultValue: "Gratis" })
                  : `Rp ${(tx.amount || 0).toLocaleString("id-ID")}`;
                return (
                  <motion.div key={tx.order_id || tx.external_id} variants={item}
                    className="flex items-center gap-4 p-4 rounded-md border border-border bg-card hover:border-brand-primary/20 transition-colors"
                    data-testid={`txn-${tx.order_id}`}>
                    {/* Status badge */}
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-mono flex-shrink-0 ${st.color} ${st.border} ${st.bg}`}>
                      <Icon className="w-3 h-3" />
                      {t(st.key, { defaultValue: isTrial ? "Trial Aktif" : undefined })}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-heading font-semibold text-sm ${planColor}`}>
                          {isTrial
                            ? `${t("payment.plan", { defaultValue: "Paket" })} ${PLAN_LABELS[tx.plan_id] || tx.plan_id || "—"} (Trial)`
                            : tx.type === "credits" || tx.type === "auto_recharge"
                            ? `${tx.credits || 0} Kredit`
                            : `${t("payment.plan")} ${PLAN_LABELS[tx.plan_id] || tx.plan_id || "—"}`}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground">•</span>
                        <span className="text-xs font-mono text-muted-foreground truncate">
                          {tx.order_id || "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {tx.created_at ? new Date(tx.created_at).toLocaleString("id-ID", {
                            day: "numeric", month: "long", year: "numeric",
                            hour: "2-digit", minute: "2-digit"
                          }) : "—"}
                        </span>
                        {tx.payment_method && (
                          <span className="text-xs font-mono bg-secondary px-1.5 py-0.5 rounded text-muted-foreground">
                            {tx.payment_method}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-heading font-bold text-sm">
                        {amountLabel}
                      </p>
                      <p className="text-xs font-mono text-muted-foreground">{tx.currency || "IDR"}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </>
        )}
      </div>
    </main>
  );
}
